import { Component, computed, input, output } from '@angular/core';
import { PaginatorModule, PaginatorState } from 'primeng/paginator';

/**
 * The footer of a paged list: how much is on screen, the page controls, and how
 * many rows to show. One component so every list paginates the same way.
 *
 * With nothing to count it stays out of the way: the table's empty state already
 * says there is nothing. The host is the `paginator` size container styles.css
 * lays the controls out against, since how much room the footer has depends on
 * whether the rail is showing, not only on the viewport.
 */
@Component({
    selector: 'app-paginator',
    imports: [PaginatorModule],
    host: {
        '[hidden]': 'totalRecords() === 0'
    },
    template: `
        <p-paginator
            [first]="first()"
            [rows]="rows()"
            [totalRecords]="totalRecords()"
            [rowsPerPageOptions]="rowsPerPageOptions()"
            [showFirstLastIcon]="true"
            [showCurrentPageReport]="true"
            [currentPageReportTemplate]="reportTemplate()"
            class="app-paginator"
            appendTo="body"
            (onPageChange)="pageChange.emit($event)" />
    `,
    styles: `
        :host {
            display: block;
            container: paginator / inline-size;
            border-top: 1px solid var(--p-content-border-color);
        }

        :host([hidden]) {
            display: none;
        }
    `
})
export class PaginatorComponent {
    readonly first = input(0);
    readonly rows = input(10);
    readonly totalRecords = input(0);
    readonly rowsPerPageOptions = input<number[]>([10, 20, 50]);
    /** What the rows are called in the report line: "de 30 pedidos". */
    readonly entryLabel = input.required<string>();
    /** What a single row is called, for a list of one: "Mostrando 1 pedido", not "1 a 1 de 1 pedidos". */
    readonly entryLabelSingular = input.required<string>();

    readonly pageChange = output<PaginatorState>();

    /** PrimeNG fills in {first}, {last} and {totalRecords}. */
    protected readonly reportTemplate = computed(() =>
        this.totalRecords() === 1
            ? `Mostrando 1 ${this.entryLabelSingular()}`
            : `Mostrando {first} a {last} de {totalRecords} ${this.entryLabel()}`
    );
}
